// JSON events of Otto Live Protocol v1, both directions.
//
// Every text WebSocket message is ONE JSON object with a `type`. Binary
// messages are audio (see audio.ts). Nothing here names a voice vendor: the
// browser never learns whether Otto runs on Gemini Live or a cascade.
//
// Session shape:
//   1. Browser opens wss://<host>/live/<consult_id> offering the subprotocols
//      from liveProtocols(live_token): LIVE_SUBPROTOCOL and the signed token as
//      `otto-token.<live_token>`, so the otto-live Worker checks the token
//      before any Durable Object wakes (design 3.2 row 5). The server echoes
//      only LIVE_SUBPROTOCOL. The browser then sends `hello` (or `resume`
//      after a drop) first; the token is checked again there.
//   2. otto-live answers `ready`, or `error` then closes.
//   3. Audio flows both ways; the server pushes `state`, transcripts and
//      speaking events; the browser may send `mode`, `extension_answer`,
//      `telemetry`, `ping`, and finally `end`.
//   4. The server always has the last word: `ended {reason}`, then it closes.

import { z } from "zod";
import { MAX_TEXT_MESSAGE_BYTES } from "./audio";

export const PROTOCOL_VERSION = 1 as const;
/** Sec-WebSocket-Protocol value. The server echoes it; a mismatch is refused. */
export const LIVE_SUBPROTOCOL = "otto-live.v1" as const;
/** Path prefix of the live socket on otto-live. */
export const LIVE_PATH_PREFIX = "/live/" as const;
/**
 * Prefix of the second Sec-WebSocket-Protocol entry that carries the signed
 * live token at the upgrade. A browser WebSocket cannot set headers, and a
 * query string would put the token in URLs and logs, so the subprotocol list
 * is the one place it can travel. Every character of a live token
 * ("v1.<base64url>.<base64url>") is a valid subprotocol token character.
 */
export const EDGE_TOKEN_PROTOCOL_PREFIX = "otto-token." as const;

/** The subprotocols the browser offers: the protocol version, then the token when it has one. */
export function liveProtocols(liveToken?: string | null): string[] {
  return liveToken ? [LIVE_SUBPROTOCOL, `${EDGE_TOKEN_PROTOCOL_PREFIX}${liveToken}`] : [LIVE_SUBPROTOCOL];
}

/** The token offered as `otto-token.<token>` in a Sec-WebSocket-Protocol header value, or null. */
export function edgeTokenFromProtocols(header: string | null | undefined): string | null {
  const entry = (header ?? "")
    .split(",")
    .map((p) => p.trim())
    .find((p) => p.startsWith(EDGE_TOKEN_PROTOCOL_PREFIX));
  return entry ? entry.slice(EDGE_TOKEN_PROTOCOL_PREFIX.length) || null : null;
}

const Version = z.literal(PROTOCOL_VERSION);

/** The consult id minted at check-in (design 8.1): a UUID. */
export const ConsultId = z.uuid();
export type ConsultId = z.infer<typeof ConsultId>;

/** Video shows the 3D Otto; voice is voice only (visual off). */
export const Mode = z.enum(["video", "voice"]);
export type Mode = z.infer<typeof Mode>;

/** Monotonic Otto turn counter within one consult, starting at 0. */
const TurnNumber = z.number().int().min(0);

// ---------------------------------------------------------------------------
// Browser -> otto-live
// ---------------------------------------------------------------------------

/** Counts-only device facts; never personal data (design 9.8). */
export const ClientInfo = z
  .object({
    device_class: z.enum(["phone", "tablet", "desktop", "unknown"]),
    in_app_browser: z.boolean(),
    output_route: z.enum(["wired", "bluetooth", "speaker", "unknown"]),
  })
  .partial();
export type ClientInfo = z.infer<typeof ClientInfo>;

export const HelloEvent = z.object({
  type: z.literal("hello"),
  v: Version,
  consult_id: ConsultId,
  /** Signed by otto-app at check-in (live-token.ts). */
  live_token: z.string().min(1).max(2048),
  mode: Mode,
  client: ClientInfo.optional(),
});

/** Sent instead of `hello` when the browser reconnects after a drop. */
export const ResumeEvent = z.object({
  type: z.literal("resume"),
  v: Version,
  consult_id: ConsultId,
  live_token: z.string().min(1).max(2048),
  mode: Mode,
  /** How long the browser was gone, as it measured it. */
  gap_ms: z.number().int().min(0),
  /** Last Otto turn the browser finished playing, if any. */
  last_turn_played: TurnNumber.nullable(),
  client: ClientInfo.optional(),
});

/** Why the BROWSER asks to end. The server decides the final EndReason. */
export const ClientEndReason = z.enum([
  "owner_ended", // the End button
  "withdrew_consent", // "stop, and do not keep this" (design 5.8, 9.12)
  "page_unload", // tab closing; best effort only
]);
export type ClientEndReason = z.infer<typeof ClientEndReason>;

export const EndEvent = z.object({
  type: z.literal("end"),
  reason: ClientEndReason,
});

/** The owner toggled video / voice only during the call. */
export const ModeEvent = z.object({
  type: z.literal("mode"),
  mode: Mode,
});

/** The owner's on-screen answer to "do you have time to keep going?". */
export const ExtensionAnswerEvent = z.object({
  type: z.literal("extension_answer"),
  accept: z.boolean(),
});

/** About once a minute (design 7.4). Counts only. */
export const TelemetryEvent = z.object({
  type: z.literal("telemetry"),
  avatar_load_ms: z.number().int().min(0).optional(),
  fps_avg: z.number().min(0).max(240).optional(),
  webgl_context_lost: z.boolean().optional(),
  fell_back_to_2d: z.boolean().optional(),
  output_route: ClientInfo.shape.output_route.optional(),
  /**
   * What the owner actually waited (Oct 1 2026): from their last word to the first sound of Otto's reply
   * `heard_turn`, measured in the browser, so phone, laptop, Wi-Fi, cellular and AirPods can be compared.
   */
  heard_turn: TurnNumber.optional(),
  heard_ms: z.number().int().min(0).max(60_000).optional(),
  /** The last ping round trip, ms. */
  rtt_ms: z.number().int().min(0).max(60_000).optional(),
});

export const PingEvent = z.object({
  type: z.literal("ping"),
  /** Browser clock, ms. Echoed in `pong` for a round trip measurement. */
  t: z.number(),
});

export const ClientEvent = z.discriminatedUnion("type", [
  HelloEvent,
  ResumeEvent,
  EndEvent,
  ModeEvent,
  ExtensionAnswerEvent,
  TelemetryEvent,
  PingEvent,
]);
export type ClientEvent = z.infer<typeof ClientEvent>;
export type HelloEvent = z.infer<typeof HelloEvent>;
export type ResumeEvent = z.infer<typeof ResumeEvent>;

// ---------------------------------------------------------------------------
// otto-live -> browser
// ---------------------------------------------------------------------------

/**
 * The 3D Otto's expression cue for the current stage (design 7.2: greeting
 * warm, pains sympathetic, close delighted). Which stage gets which mood is
 * data (Agenda.stage_moods in @otto/config); the browser maps a mood to the
 * eyelids and smile. Keep in step with AGENDA_MOODS in @otto/config.
 */
export const AVATAR_MOODS = ["neutral", "warm", "sympathetic", "surprised", "delighted"] as const;
export const AvatarMood = z.enum(AVATAR_MOODS);
export type AvatarMood = z.infer<typeof AvatarMood>;

/** One agenda item as the owner sees it on screen. Labels come from config. */
export const AgendaItemView = z.object({
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(80),
});
export type AgendaItemView = z.infer<typeof AgendaItemView>;

export const ReadyEvent = z.object({
  type: z.literal("ready"),
  v: Version,
  consult_id: ConsultId,
  resumed: z.boolean(),
  mode: Mode,
  agenda: z.array(AgendaItemView),
  /** The clock is a guide; these let the UI place the agenda, not count down. */
  target_minutes: z.number().positive(),
  cap_minutes: z.number().positive(),
});

/**
 * Pushed on every stage change, agenda change, extension change, pause, and
 * at least every 5 seconds while connected so the clock stays honest.
 */
export const StateEvent = z.object({
  type: z.literal("state"),
  /** Stage id from the active Agenda config (not a fixed enum: it is data). */
  stage: z.string().min(1).max(64),
  agenda_index: z.number().int().min(0),
  /** Connected seconds; the clock pauses while the owner is disconnected. */
  clock_seconds: z.number().int().min(0),
  paused: z.boolean(),
  /** Otto has asked whether the owner has time to keep going. */
  extension_offered: z.boolean(),
  /** null until the owner answers (by voice or on screen). */
  extension_accepted: z.boolean().nullable(),
  /** The expression cue for this stage; absent or null keeps Otto's resting look. */
  mood: AvatarMood.nullable().optional(),
});

/**
 * Live captions of what the owner said (the voice engine's transcription).
 *
 * `text` semantics (both transcript events): while `final` is false, `text`
 * is a DELTA, the next chunk to append to that turn's caption. The one event
 * with `final: true` carries the WHOLE turn's text as saved (its last 4000
 * characters), which replaces the caption built from the deltas. A late
 * chunk for a turn already sent as final arrives as another delta (final
 * false) for that same turn.
 */
export const InputTranscriptEvent = z.object({
  type: z.literal("input_transcript"),
  turn: TurnNumber,
  text: z.string().max(4000),
  /** true on the event that carries the whole finished turn. */
  final: z.boolean(),
});

/** Live captions of what Otto said, with the same delta then whole-turn rule. Dashes are already stripped by the server. */
export const OutputTranscriptEvent = z.object({
  type: z.literal("output_transcript"),
  turn: TurnNumber,
  text: z.string().max(4000),
  final: z.boolean(),
});

export const OttoSpeakingEvent = z.object({
  type: z.literal("otto_speaking"),
  turn: TurnNumber,
});

export const OttoDoneEvent = z.object({
  type: z.literal("otto_done"),
  turn: TurnNumber,
});

/** The owner barged in. Drop queued audio for `turn` and fade out (design 4.3). */
export const InterruptedEvent = z.object({
  type: z.literal("interrupted"),
  turn: TurnNumber,
});

/** The server side of the call is recovering (voice session swap failed, restart). */
export const ReconnectingEvent = z.object({
  type: z.literal("reconnecting"),
  reason: z.enum(["voice_session", "server_restart"]),
  attempt: z.number().int().min(1),
});

/** Recovery finished; clears the "Reconnecting" state in the UI. */
export const ReconnectedEvent = z.object({
  type: z.literal("reconnected"),
});

/** Final reason a consult ended. Mirrors the package's session.end_reason. */
export const EndReason = z.enum([
  "completed", // Otto's close, then his last word played
  "owner_ended", // End button
  "time_limit", // the cap plus the close grace ran out
  "silence_timeout", // "Still with me?" went unanswered
  "connection_lost", // the owner never came back inside the hold window
  "withdrew_consent", // transcript deleted, nothing sent (design 9.12)
  "conduct", // second strike for abuse, or a prank (design 5.8)
  "spend_cap", // per consult graceful close (design 9.5)
  "admin_ended", // an admin ended the call from Live now; Otto still closes first
  "error", // unrecoverable failure; loud on the server side
]);
export type EndReason = z.infer<typeof EndReason>;

export const EndedEvent = z.object({
  type: z.literal("ended"),
  reason: EndReason,
});

export const LiveErrorCode = z.enum([
  "bad_message", // not JSON, unknown type, failed validation, oversize
  "hello_required", // the first message was not hello or resume
  "bad_token", // signature invalid or token malformed
  "token_expired",
  "wrong_consult", // token is for another consult
  "consult_not_found", // otto-app never initialised this consult
  "consult_ended", // the consult is already over
  "already_connected", // a second live socket for the same consult
  "voice_unavailable", // the voice engine refused or failed to start
  "at_capacity",
  "not_implemented",
  "internal",
]);
export type LiveErrorCode = z.infer<typeof LiveErrorCode>;

export const ErrorEvent = z.object({
  type: z.literal("error"),
  code: LiveErrorCode,
  /** Plain words, safe to show. No dashes, no vendor names, no secrets. */
  message: z.string().max(500),
  retryable: z.boolean(),
});

export const PongEvent = z.object({
  type: z.literal("pong"),
  t: z.number(),
});

export const ServerEvent = z.discriminatedUnion("type", [
  ReadyEvent,
  StateEvent,
  InputTranscriptEvent,
  OutputTranscriptEvent,
  OttoSpeakingEvent,
  OttoDoneEvent,
  InterruptedEvent,
  ReconnectingEvent,
  ReconnectedEvent,
  EndedEvent,
  ErrorEvent,
  PongEvent,
]);
export type ServerEvent = z.infer<typeof ServerEvent>;
export type ReadyEvent = z.infer<typeof ReadyEvent>;
export type StateEvent = z.infer<typeof StateEvent>;
export type ErrorEvent = z.infer<typeof ErrorEvent>;

// ---------------------------------------------------------------------------
// Parsing and encoding
// ---------------------------------------------------------------------------

export type ParseResult<T> = { ok: true; event: T } | { ok: false; error: string };

function parseWith<T>(schema: z.ZodType<T>, text: string): ParseResult<T> {
  // String length (UTF-16 units) is a close enough stand in for bytes here.
  if (text.length > MAX_TEXT_MESSAGE_BYTES) return { ok: false, error: "message too large" };
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, error: "not JSON" };
  }
  const result = schema.safeParse(raw);
  if (!result.success) {
    // Paths and messages only; never echo the payload (it may hold a token).
    const detail = result.error.issues
      .slice(0, 3)
      .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
      .join("; ");
    return { ok: false, error: detail };
  }
  return { ok: true, event: result.data };
}

/** Used by otto-live on every text message from the browser. */
export function parseClientEvent(text: string): ParseResult<ClientEvent> {
  return parseWith(ClientEvent, text);
}

/** Used by the browser on every text message from otto-live. */
export function parseServerEvent(text: string): ParseResult<ServerEvent> {
  return parseWith(ServerEvent, text);
}

/** Serialize an event. Kept as a function so both sides encode one way. */
export function encodeEvent(event: ClientEvent | ServerEvent): string {
  return JSON.stringify(event);
}

/** The live socket path for a consult: /live/<consult_id>. */
export function livePath(consultId: string): string {
  return `${LIVE_PATH_PREFIX}${consultId}`;
}

/** Returns the consult id when `pathname` is a live socket path, else null. */
export function matchLivePath(pathname: string): string | null {
  if (!pathname.startsWith(LIVE_PATH_PREFIX)) return null;
  const rest = pathname.slice(LIVE_PATH_PREFIX.length);
  return ConsultId.safeParse(rest).success ? rest : null;
}
